import React, { useState } from 'react';
import Image from 'next/image';
import { message, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatNumber } from '@/lib/utils';
import { imgUrl, proxyImgUrl } from '@/shared/utils';
import SingleResult from '../single-result';
import styles from './ai-result.module.less';
import AIUpgradeModal from './AIUpgradeModal';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { useTranslation } from 'next-i18next';

// 搜索结果项类型
export interface SearchResultItem {
  id: number;
  avatar: string;
  username: string;
  fullName: string;
  email: string;
  verified: boolean;
  description: string;
  tags: string[];
  followerCount: number;
  followingCount: number;
  matchType: number;
}

interface ResultTableProps {
  dataSource: SearchResultItem[];
  userCreditInfo?: {
    has_subscription?: boolean;
    expire_time?: string;
  };
}

export default function ResultTable({ dataSource, userCreditInfo }: ResultTableProps) {
  const { t } = useTranslation('common');
  // 判断是否有有效订阅
  const hasValidSubscription =
    userCreditInfo?.has_subscription &&
    (!userCreditInfo?.expire_time || new Date(userCreditInfo.expire_time) >= new Date());

  // 记录哪些行已经被点击过（仅订阅用户）
  const [clickedEmailIds, setClickedEmailIds] = React.useState<Set<number>>(new Set());

  // 升级弹窗状态
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // View Email 按钮点击处理
  const handleViewEmail = (id: number, email: string) => {
    ga_logEvent(EventName.ai_discover_view_email_click);
    if (hasValidSubscription && email) {
      // 有订阅且有邮箱，记录这一行已被点击，之后一直显示邮箱
      setClickedEmailIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(id);
        return newSet;
      });
    } else {
      // 没有订阅，显示升级弹窗
      setShowUpgradeModal(true);
    }
  };

  // 复制邮箱到剪贴板
  const handleCopyEmail = (email: string) => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        message.success(t('Email copied'));
      })
      .catch(() => {
        message.error(t('Failed to copy email'));
      });
  };
  // 定义 Table 列
  const columns: ColumnsType<SearchResultItem> = [
    {
      title: t('Account'),
      key: 'account',
      width: '35%',
      render: (record: SearchResultItem, _, index) => (
        <div className="flex items-center gap-4 py-2 max-w-full">
          <div className="text-black font-semibold text-sm">{index + 1}</div>
          <div className="relative size-12 rounded-full border border-[#D9D9D9] overflow-hidden flex-shrink-0">
            <a href={`https://www.instagram.com/${record.username}`} target="_blank" rel="noopener noreferrer">
              <Image
                src={imgUrl(proxyImgUrl(record.avatar), 'small')}
                alt="user avatar"
                width={48}
                height={48}
                className="object-cover"
              />
            </a>
          </div>
          <div className="flex flex-col justify-center flex-1 min-w-0">
            <div className="mb-1 w-full overflow-hidden">
              <div className="text-[#0C0C0C] font-bold text-base leading-[1.21em] font-[Inter] overflow-hidden text-ellipsis whitespace-nowrap">
                {record.fullName}
              </div>
              <div className="text-[#0C0C0C] font-normal text-base leading-[1.21em] font-[Inter] opacity-60 flex items-center gap-1 overflow-hidden">
                <a href={`https://www.instagram.com/${record.username}`} target="_blank" rel="noopener noreferrer">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">@{record.username}</span>
                </a>
                <Image
                  src="/images/account_checked.svg"
                  alt=""
                  width={16}
                  height={16}
                  className="flex-shrink-0 size-4 object-cover"
                />
              </div>
            </div>
            <Tooltip title={record.description}>
              <p className="text-[#717182] font-normal text-xs leading-[1.33em] font-[Inter] mb-2 line-clamp-2 cursor-pointer">
                {record.description}
              </p>
            </Tooltip>
            {clickedEmailIds.has(record.id) ? (
              // 显示邮箱的白色按钮（点击后复制）
              <button
                onClick={() => handleCopyEmail(record.email)}
                className="flex items-center gap-1.5 bg-white border border-[#D9D9D9] hover:bg-[#F6F5FF] transition px-2.5 py-2 rounded-lg w-fit"
              >
                <span className="text-[#0C0C0C] font-medium text-xs">{record.email}</span>
              </button>
            ) : (
              // View Email 按钮
              <button
                onClick={() => handleViewEmail(record.id, record.email)}
                className="flex items-center gap-1.5 bg-[#030213] hover:bg-[#725CFF] transition text-white px-2.5 py-2 rounded-lg w-fit"
              >
                <Image src="/images/instagram-email-finder/email-icon.svg" alt="email" width={16} height={16} />
                <span className="text-white font-medium text-xs">{t('View Email')}</span>
              </button>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('Categories'),
      dataIndex: 'tags',
      key: 'tags',
      width: '15%',
      render: (tags: string[]) => (
        <div className="flex flex-wrap items-center gap-1">
          {tags.map((tag, index) => (
            <div key={index} className="px-2 border border-black/10 rounded-[20px]">
              <span className="text-xs font-medium">{tag}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('Followers'),
      dataIndex: 'followerCount',
      key: 'followerCount',
      width: '12%',
      align: 'center',
      render: (count: number) => <span className="text-sm font-normal">{formatNumber(count)}</span>,
    },
    {
      title: t('Following'),
      dataIndex: 'followingCount',
      key: 'followingCount',
      width: '12%',
      align: 'center',
      render: (count: number) => <span className="text-sm font-normal">{formatNumber(count)}</span>,
    },
    {
      title: t('Match Score'),
      key: 'matchScore',
      width: '15%',
      align: 'center',
      render: (record: SearchResultItem) => {
        const isHighMatch = record.matchType === 1;
        const textColor = isHighMatch ? 'text-[#38C13D]' : 'text-[#FF9500]';
        const matchText = isHighMatch ? t('High') : t('Medium');

        return (
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-1">
              <span className={`${textColor} text-base`}>✓</span>
              <span className={`${textColor} font-normal text-sm`}>
                <span className="font-semibold mr-[0.5em]">{matchText}</span>
                {t('Match')}
              </span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* PC 端：使用 Table 组件 */}
      <div className="hidden lg:block">
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          pagination={false}
          tableLayout="fixed"
          className={styles.aiResultTable}
        />
      </div>

      {/* 移动端：使用原来的列表 */}
      <div className="lg:hidden flex flex-col mb-8">
        {dataSource.map((item) => (
          <SingleResult
            key={item.id}
            data={item}
            userCreditInfo={userCreditInfo}
            isEmailVisible={clickedEmailIds.has(item.id)}
            onViewEmail={() => handleViewEmail(item.id, item.email)}
          />
        ))}
      </div>

      {/* 升级弹窗 - View Email */}
      <AIUpgradeModal
        open={showUpgradeModal}
        title="Upgrade Required"
        desc="Email lookup is a Premium-only feature. Please upgrade to access email details."
        btnTitle="Upgrade Now"
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          ga_logEvent(EventName.ai_discover_view_email_upgrade_click);
          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </>
  );
}
